import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { TabelaBase } from '../../../../../components/formControls/TabelaBase';
import { OuvidoriaHistoricoList } from '../types/Ouvidoria';
import { useGetOuvidoriasHistoricosQuery } from '../ouvidoriaApiSlice';

interface Props {
  id: string | undefined | null;
  historico?: OuvidoriaHistoricoList[] | undefined | null;
}

export const OuvidoriaHistoricoDataTable = ({ id, historico }: Props) => {
  const [ouvidoriaHistoricos, setOuvidoriaHistoricos] = useState<OuvidoriaHistoricoList[]>([]);

  const {
    data: dataOuvidoriaHistorico,
    isFetching: isFetchingHistorico,
    error: errorHistorico
  } = useGetOuvidoriasHistoricosQuery(id ?? '', {skip: !id});

  useEffect(() => {
    if (dataOuvidoriaHistorico) {
      setOuvidoriaHistoricos(dataOuvidoriaHistorico as OuvidoriaHistoricoList[]);
    }
    if(historico){
      setOuvidoriaHistoricos(historico);
    }
 }, [historico, dataOuvidoriaHistorico]);


  const renderObservacao = (rowData: OuvidoriaHistoricoList) => {
    return (
      <Typography color="secondary" variant="body2">
        {rowData.observacao !== null ? rowData.observacao : 'N/A'}
      </Typography>
    );
  };

  const renderUsuario = (rowData: OuvidoriaHistoricoList) => (
    <Typography color="secondary" variant="body2">
      {rowData.usuario?.nome || 'Usuário não disponível'}
    </Typography>
  );

  const renderData = (rowData: OuvidoriaHistoricoList) => {
    const dataCriacao = rowData.dataHora
      ? format(rowData.dataHora, 'dd/MM/yyyy HH:mm:ss')
      : 'Data não disponível';

    return (
      <Typography color="secondary" variant="body2">
        {dataCriacao}
      </Typography>
    );
  };

  const visualizarObservacao = (rowData: OuvidoriaHistoricoList) => {
    const observacao = rowData.observacao || 'Nenhuma observação informada.';

    const novaJanela = window.open(
      '',
      '_blank',
      'width=900,height=700,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
    );

    if (!novaJanela) {
      alert('Não foi possível abrir a janela. Verifique se o navegador está bloqueando pop-ups.');
      return;
    }

    const data = rowData.dataHora
      ? format(rowData.dataHora, 'dd/MM/yyyy HH:mm:ss')
      : 'Data não disponível';

    const usuario = rowData.usuario?.nome || 'Usuário não disponível';

    // Evita problemas caso a observação contenha HTML.
    const escapeHtml = (texto: string) => {
      return texto
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    novaJanela.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>Observação - Ouvidoria</title>

        <style>
          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            padding: 30px;
            font-family: Arial, Helvetica, sans-serif;
            background: #f5f5f5;
            color: #333;
          }

          .container {
            max-width: 850px;
            margin: 0 auto;
            background: white;
            padding: 35px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }

          h1 {
            margin-top: 0;
            margin-bottom: 25px;
            font-size: 24px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 15px;
          }

          .informacoes {
            margin-bottom: 25px;
            font-size: 14px;
            color: #666;
          }

          .informacoes div {
            margin-bottom: 6px;
          }

          .observacao {
            border: 1px solid #ddd;
            border-radius: 5px;
            padding: 20px;
            min-height: 250px;
            font-size: 16px;
            line-height: 1.6;
            white-space: pre-wrap;
            word-wrap: break-word;
          }

          .acoes {
            margin-top: 25px;
            text-align: right;
          }

          button {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            background: #1976d2;
            color: white;
            font-size: 14px;
            cursor: pointer;
          }

          button:hover {
            background: #1565c0;
          }

          @media print {
            body {
              background: white;
              padding: 0;
            }

            .container {
              max-width: none;
              box-shadow: none;
              padding: 0;
            }

            .acoes {
              display: none;
            }

            .observacao {
              border: none;
              padding: 0;
            }
          }
        </style>
      </head>

      <body>
        <div class="container">

          <h1>Observação - Histórico da Ouvidoria</h1>

          <div class="informacoes">
            <div><strong>Data:</strong> ${escapeHtml(data)}</div>
            <div><strong>Usuário:</strong> ${escapeHtml(usuario)}</div>
          </div>

          <div class="observacao">${escapeHtml(observacao)}</div>

          <div class="acoes">
            <button onclick="window.print()">
              Imprimir
            </button>
          </div>

        </div>
      </body>
      </html>
    `);

    novaJanela.document.close();
    novaJanela.focus();
  };

  const renderAction = (rowData: OuvidoriaHistoricoList) => (
    <Box display="flex" gap={1}>
      <Tooltip title="Visualizar">
        <IconButton
          color="secondary"
          aria-label="visualizar"
          onClick={() => visualizarObservacao(rowData)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const columns = [
    {
      header: 'Data',
      body: renderData,
      sortable: true,
      filter: false,
      filterPlaceholder: 'Filtrar por data',
      style: {
        width: '15%'
      }
    },
    {
      header: 'Observação',
      body: renderObservacao,
      sortable: true,
      filter: false,
      filterPlaceholder: 'Filtrar por observação',
      style: {
        width: '55%'
      }
    },
    {
      header: 'Usuário',
      body: renderUsuario,
      sortable: true,
      filter: false,
      filterPlaceholder: 'Filtrar por nome',
      style: {
        width: '20%'
      }
    },
    {
      header: 'Ações',
      body: renderAction,
      style: {
        width: '10%'
      }
    }
  ];

  const headerGerados = (
    <Typography variant="body2" color="textSecondary">
      Registro(s) encontrado(s): {ouvidoriaHistoricos.length}
    </Typography>
  );

  return (
    <>
      <TabelaBase
        value={ouvidoriaHistoricos}
        columns={columns}
        dataKey="id"
        header={headerGerados}
        rows={25}
        paginator
        exportCSV={false}
        scrollable
        scrollHeight="500px"
        frozenWidth="150px"
        themeClassName="meu-tema-customizado"
      />
    </>
  );
};